import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import { AiAssistantV2Store } from '@bizzdesign/ai-assistant/data-access';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { DefaultDialogSize, DialogProperties } from '@bizzdesign/shared/models';
import { DialogComponent, DrawerGlobalStore } from '@bizzdesign/ui-components';
import type {
  InsightData,
  InsightSuggestion,
  NavigateData,
} from '@bizzdesign/ai-assistant/workflows';
import { generateUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import type {
  ChatSessionRename,
  ChatSessionScope,
  ChatSessionSort,
} from '@bizzdesign/ai-assistant/data-access';
import type { FileUIPart } from 'ai';
import {
  ContentOpenContainerComponent,
  type ContentOpenDialogChoice,
  ContentOpenService,
  newWorkspace,
} from '@bizzdesign/feature-content-open';
import {
  LangfuseFeedbackService,
  LayoutContextService,
  ProductAnalyticsService,
} from '@bizzdesign/shared/services';
import { TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  tap,
  type Observable,
} from 'rxjs';
import {
  AiAssistantV2Component,
  type FeedbackEmission,
  type MessageSendPayload,
  type OptionSelection,
  type OptionSendResult,
  type RejectedAttachment,
} from '../../components/ai-assistant-v2/ai-assistant-v2.component';
import { FrameOverlayToolbarComponent } from '../../components/frame-overlay-toolbar.component/frame-overlay-toolbar.component';
import { AssistantNavigationService } from '../../services/assistant-navigation.service';

@Component({
  selector: 'bd-ai-assistant-container-v2',
  standalone: true,
  imports: [
    AiAssistantV2Component,
    AsyncPipe,
    DialogComponent,
    FrameOverlayToolbarComponent,
    ContentOpenContainerComponent,
    SharedI18nModule,
  ],
  templateUrl: './ai-assistant-container-v2.component.html',
  styleUrl: './ai-assistant-container-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Connects the assistant store, feature flags, and analytics to the chat panel UI.
 */
export class AiAssistantContainerV2Component {
  private readonly assistantStore = inject(AiAssistantV2Store);
  private readonly featureFlags = inject(FeatureFlagsStore);
  private readonly feedbackService = inject(LangfuseFeedbackService);
  private readonly contentOpenService = inject(ContentOpenService);
  private readonly layoutContextService = inject(LayoutContextService);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly drawerStore = inject(DrawerGlobalStore);
  private readonly hostElement: HTMLElement = inject(ElementRef).nativeElement;
  private readonly assistantNavigationService = inject(
    AssistantNavigationService,
  );
  private readonly destroyRef = inject(DestroyRef);

  protected readonly chat = this.assistantStore.chat;
  protected readonly thinkingNote = this.assistantStore.thinkingNote;
  protected readonly plan = this.assistantStore.plan;
  protected readonly continuationPending =
    this.assistantStore.isContinuationPending;
  protected readonly awaitingInstructions =
    this.assistantStore.awaitingInstructions;
  protected readonly isUploadingAttachments =
    this.assistantStore.isUploadingAttachments;
  protected readonly localAttachmentPreviews =
    this.assistantStore.localAttachmentPreviews;
  protected readonly promptSuggestions = this.assistantStore.promptSuggestions;
  protected readonly insightSuggestionsState =
    this.assistantStore.insightSuggestionsState;
  private readonly insightSuggestionsEnabled =
    this.featureFlags.enableDesignAssistantInsightSuggestions;
  protected readonly insightSuggestions = computed(() =>
    this.insightSuggestionsEnabled()
      ? this.insightSuggestionsState().suggestions
      : [],
  );
  protected readonly areInsightSuggestionsLoading = computed(
    () =>
      this.insightSuggestionsEnabled() &&
      this.insightSuggestionsState().status === 'loading',
  );
  protected readonly insightData = this.assistantStore.insightData;
  protected readonly canSaveToWorkspace =
    this.assistantStore.canSaveToWorkspace;
  protected readonly showSuggestionsPanel = computed(
    () =>
      this.insightSuggestionsEnabled() &&
      this.assistantStore.context() !== null,
  );

  protected readonly feedbackConfig = this.featureFlags.aiAssistantFeedbackV2;
  protected readonly fileUploadEnabled =
    this.featureFlags.aiAssistantFileUploadEnabled;
  protected readonly deliverablePersistenceEnabled =
    this.featureFlags.aiAssistantDeliverablePersistence;
  protected readonly chatSessions = this.assistantStore.chatSessions;
  protected readonly chatSessionsStatus =
    this.assistantStore.chatSessionsStatus;
  protected readonly chatSessionsHasMore =
    this.assistantStore.chatSessionsHasMore;
  protected readonly chatSessionsSearchTerm =
    this.assistantStore.chatSessionsSearchTerm;
  protected readonly chatSessionsSort = this.assistantStore.chatSessionsSort;
  protected readonly chatSessionsScope = this.assistantStore.chatSessionsScope;
  protected readonly hasFrameContext = computed(
    () => this.assistantStore.context()?.type === 'design',
  );
  protected readonly activeChatSessionId =
    this.assistantStore.activeChatSessionId;
  protected readonly chatSessionLoadingId =
    this.assistantStore.chatSessionLoadingId;
  protected readonly chatSessionDeletingIds =
    this.assistantStore.chatSessionDeletingIds;
  protected readonly chatSessionRenamingId =
    this.assistantStore.chatSessionRenamingId;
  protected readonly assistantPanelVisible =
    this.assistantStore.isAssistantPanelVisible;
  protected readonly chatHistoryPanelOpen = signal(false);
  protected readonly optionSendResult = signal<OptionSendResult | null>(null);

  private get prompted(): 'voluntary' | 'soft' {
    return this.feedbackConfig() === null ? 'voluntary' : 'soft';
  }

  readonly layoutContext$ = this.layoutContextService.context$;

  constructor() {
    effect(() => {
      const event = this.drawerStore.animationDone();
      if (event?.position === 'right' && event.toState === 'in') {
        this.hostElement
          .querySelector<HTMLElement>('[data-id="assistant-chat-input"]')
          ?.focus();
      }
    });
    effect(() => {
      if (!this.assistantPanelVisible()) {
        this.chatHistoryPanelOpen.set(false);
      }
    });
    effect(() => {
      if (!this.hasFrameContext() && this.chatSessionsScope() !== 'all') {
        this.assistantStore.setChatSessionsScope('all');
      }
    });
    effect(() => {
      if (
        !this.deliverablePersistenceEnabled() ||
        this.assistantStore.context() === null ||
        this.chatSessionsStatus() !== 'idle'
      ) {
        return;
      }
      this.assistantStore.loadChatSessions();
    });
    this.assistantStore.setNavigation(this.assistantNavigationService);
    this.destroyRef.onDestroy(() => {
      this.assistantStore.setNavigation(null);
    });
  }
  readonly workspacePickerDialogVisible$ = new BehaviorSubject<boolean>(false);
  readonly clearChatDialogVisible$ = new BehaviorSubject<boolean>(false);
  readonly clearChatDialogProperties$ = this.translocoService
    .selectTranslateObject('dialog.clearChat', {}, 'featureAiAssistant')
    .pipe(
      map(
        (translations) =>
          new DialogProperties({
            defaultFooter: {
              footerConfirmButtonLabel: translations.confirm,
              footerDismissButtonLabel: translations.dismiss,
            },
            defaultHeader: { headerTitle: translations.title },
            defaultSize: DefaultDialogSize.SMALL,
            defaultStyle: 'error',
          }),
      ),
    );

  onMessageSent(payload: MessageSendPayload): void {
    void this.assistantStore.sendMessage(payload.text, payload.files);
  }

  onNavigationRequested(navigation: NavigateData): void {
    this.assistantNavigationService.navigateTo(navigation);
  }

  onAttachmentsRejected(rejected: RejectedAttachment[]): void {
    rejected.forEach((attachment) =>
      this.productAnalyticsService.sendEvent(
        'assistant_attachment_failed',
        attachment,
      ),
    );
  }

  onDownloadAttachment(part: FileUIPart): void {
    void this.assistantStore.downloadAttachment(part);
  }

  onRequestAttachmentPreview(part: FileUIPart): void {
    void this.assistantStore.resolveAttachmentPreview(part);
  }

  onStopRequested(): void {
    this.assistantStore.stopChat();
  }

  onClearRequested(): void {
    if (this.deliverablePersistenceEnabled()) {
      this.assistantStore.clearChat();
      return;
    }
    this.clearChatDialogVisible$.next(true);
  }

  onClearChatConfirm(): void {
    this.assistantStore.clearChat();
    this.clearChatDialogVisible$.next(false);
  }

  onChatHistoryOpened(): void {
    this.assistantStore.loadChatSessions();
  }

  onChatSessionsLoadMoreRequested(): void {
    this.assistantStore.loadMoreChatSessions();
  }

  onChatSessionsSearchRequested(searchTerm: string): void {
    this.assistantStore.setChatSessionsSearchTerm(searchTerm);
  }

  onChatSessionsSortChanged(sort: ChatSessionSort): void {
    this.assistantStore.setChatSessionsSort(sort);
  }

  onChatSessionsScopeChanged(scope: ChatSessionScope): void {
    this.assistantStore.setChatSessionsScope(scope);
  }

  onChatSessionSelected(deliverableId: UUID): void {
    this.assistantStore.resumeChatSession(deliverableId);
  }

  onChatSessionsDeleteRequested(deliverableIds: UUID[]): void {
    this.assistantStore.deleteChatSessions(deliverableIds);
  }

  onChatSessionsRenameRequested(rename: ChatSessionRename): void {
    this.assistantStore.renameChatSession(rename);
  }

  onSuggestionClicked(prompt: string): void {
    void this.assistantStore.sendMessage(prompt);
  }

  async onOptionSelected(selection: OptionSelection): Promise<void> {
    const success = await this.assistantStore.sendMessage(selection.label);
    this.optionSendResult.set({
      selectionId: selection.selectionId,
      messageId: selection.messageId,
      success,
    });
  }

  onSelectedVisualSuggestion(suggestion: InsightSuggestion): void {
    this.productAnalyticsService.sendEvent('insight_suggestion_selected', {
      chart_type: suggestion.chartType,
    });
    void this.assistantStore.sendMessage(suggestion.insightConfigurorPrompt);
  }

  onThumbsUp(emission: FeedbackEmission): void {
    this.submitFeedbackScore(emission, 1);
  }

  onThumbsDown(emission: FeedbackEmission): void {
    this.submitFeedbackScore(emission, 0);
  }

  onFeedbackPrompted(payload: { messageId: string }): void {
    this.productAnalyticsService.sendEvent('assistant_feedback_prompted', {
      mode: this.prompted,
      message_id: payload.messageId,
    });
  }

  onFeedbackSkipped(payload: { messageId: string }): void {
    this.productAnalyticsService.sendEvent('assistant_feedback_skipped', {
      mode: this.prompted,
      message_id: payload.messageId,
      reason: 'skip-button',
    });
  }

  private submitFeedbackScore(emission: FeedbackEmission, value: 0 | 1): void {
    const mode = emission.prompted;
    const traceId = emission.message.id;
    const responseLengthChars = this.computeResponseLengthChars(
      emission.message,
    );
    const context = {
      assistantVersion: 'v5',
      ...(responseLengthChars === undefined ? {} : { responseLengthChars }),
    };

    this.feedbackService.score({
      traceId,
      value,
      ...(emission.comment !== undefined && emission.comment !== ''
        ? { comment: emission.comment }
        : {}),
      ...(emission.categories !== undefined && emission.categories.length > 0
        ? { categories: emission.categories }
        : {}),
      ...(emission.rating === undefined ? {} : { rating: emission.rating }),
      prompted: mode,
      context,
    });

    this.productAnalyticsService.sendEvent('assistant_feedback_submitted', {
      mode,
      message_id: traceId,
      value,
      categories: emission.categories ?? [],
      has_comment: emission.comment !== undefined && emission.comment !== '',
      comment_length: emission.comment?.length ?? 0,
      ...(emission.rating === undefined ? {} : { rating: emission.rating }),
    });
  }

  private computeResponseLengthChars(message: {
    parts: ReadonlyArray<{ type: string; text?: string }>;
  }): number | undefined {
    const total = message.parts
      .filter(
        (part): part is { type: 'text'; text: string } =>
          part.type === 'text' && typeof part.text === 'string',
      )
      .reduce((sum, part) => sum + part.text.length, 0);
    return total > 0 ? total : undefined;
  }

  /**
   * Forwarded straight to the store while we're still inside the user's
   * click event handler. Keeping the call synchronous up to
   * `mcpOAuth.connect()`'s `globalThis.open` is what lets the browser
   * treat the OAuth window as a real popup instead of a background tab.
   */
  onConnectMcpClicked(prompt: {
    mcpId: string;
    mcpName: string;
    endpointUrl: string;
  }): void {
    this.assistantStore.connectMcp(prompt).catch(() => undefined);
  }

  onInsightChartClicked(insight: InsightData['insights'][number]): void {
    this.productAnalyticsService.sendEvent('insight_opened', {
      visualization_id: insight.visualizationId,
      insight_name: insight.name,
    });
    this.assistantStore.setSelectedInsight(insight);
    this.contentOpenService
      .openAiContentInOverlay(generateUUID(), insight.name)
      .pipe(
        catchError(() => of(false)),
        tap((didNavigate) => {
          if (!didNavigate) {
            this.assistantStore.clearSelectedInsight();
          }
        }),
      )
      .subscribe();
  }

  onCloseOverlay(): void {
    this.productAnalyticsService.sendEvent('insight_overlay_closed');
    this.runAfterSuccessfulNavigation(
      this.contentOpenService.closeOverlay(),
      () => {
        this.assistantStore.clearSelectedInsight();
      },
    );
  }

  onSaveToWorkspace(): void {
    this.workspacePickerDialogVisible$.next(true);
  }

  onWorkspacePickerSelectionConfirm(
    pickerChoice: ContentOpenDialogChoice,
  ): void {
    const insight = this.assistantStore.selectedInsight();
    if (insight === null) {
      return;
    }

    const openInsight$ =
      pickerChoice.selectedWorkspace === newWorkspace
        ? this.contentOpenService.openAiContentInNewWorkspace(insight.name)
        : this.contentOpenService.openAiContentInWorkspace(
            insight.name,
            pickerChoice.selectedWorkspace,
          );

    this.runAfterSuccessfulNavigation(openInsight$, () => {
      this.productAnalyticsService.sendEvent('insight_saved_to_workspace');
      this.workspacePickerDialogVisible$.next(false);
    });
  }

  private runAfterSuccessfulNavigation(
    operation$: Observable<boolean>,
    onSuccess: () => void,
  ): void {
    operation$
      .pipe(
        catchError(() => of(false)),
        filter((didNavigate): didNavigate is true => didNavigate),
        tap(onSuccess),
      )
      .subscribe();
  }
}
